import type { DigitShape } from "@positive/format";
import type { Language } from "@positive/i18n";
import { applyTheme, switchLanguage, type ThemeKey } from "@positive/ui";
import type { i18n } from "i18next";
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";

import { savePreferences, type AccountPreferenceValues, type Preferences } from "./preferences";

/** What the user's account stores (decision I6); `language: null` = tenant default. */
export interface AccountChoices {
  language: Language | null;
  digits: DigitShape | null;
  hijri: boolean | null;
}

/** Where the signed-in user's choices are stored (`PUT /api/platform/me/preferences`). */
export interface AccountPreferencesStore {
  save: (choices: AccountChoices) => Promise<void>;
  /** The save failed and the change was reverted to the account's last stored values. */
  onFailure: (error: unknown) => void;
}

interface PreferencesContextValue extends Preferences {
  /** The user picks a language (stored on the account when signed in). */
  setLanguage: (language: Language) => void;
  /** Per browser only. */
  setTheme: (theme: ThemeKey) => void;
  /** `null` returns to the tenant setting. */
  setDigits: (digits: DigitShape | null) => void;
  /** Hijri beside Gregorian dates; `null` returns to the tenant setting. */
  setHijri: (hijri: boolean | null) => void;
  /**
   * After sign-in: the account's values win over this browser (`language` = the effective language, user choice or
   * tenant default), and later changes are saved to `store` optimistically.
   */
  connectAccount: (choices: AccountChoices, language: Language, store: AccountPreferencesStore) => void;
  /** Sign-out / no session: changes stay in this browser. */
  disconnectAccount: () => void;
}

interface ConnectedAccount {
  store: AccountPreferencesStore;
  /** Choices including changes not saved yet. */
  choices: AccountChoices;
  /** Last values the account confirmed, restored when a save fails. */
  confirmed: { choices: AccountChoices; language: Language };
  /** Saves run one after another, in the order of the changes. */
  queue: Promise<void>;
  /** Number of the latest change; only a failure of the latest one reverts. */
  latest: number;
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

export function PreferencesProvider({
  i18n,
  initial,
  children,
}: {
  i18n: i18n;
  initial: Preferences;
  children: ReactNode;
}) {
  const [preferences, setPreferences] = useState(initial);
  const current = useRef(initial);
  const account = useRef<ConnectedAccount | null>(null);

  // Every change is also kept in this browser: the choice before sign-in and the instant cache of the next start.
  const update = useCallback((patch: Partial<Preferences>) => {
    const next = { ...current.current, ...patch };
    current.current = next;
    setPreferences(next);
    savePreferences(next);
  }, []);

  /**
   * Shows `values`; a language change switches texts and direction in place, no reload (ADR-0009/D-1). Changes are shown
   * one after another: a second switch made while the first one still loaded its texts compared against the old
   * language, was skipped, and the first one then won (Arabic chosen back, the page stayed English).
   */
  const shown = useRef<Promise<void>>(Promise.resolve());
  const show = useCallback(
    (values: Partial<AccountPreferenceValues>) => {
      const next = shown.current.then(async () => {
        if (values.language !== undefined && values.language !== current.current.language) {
          await switchLanguage(i18n, values.language);
        }
        update(values);
      });
      shown.current = next.catch(() => undefined);
      return next;
    },
    [i18n, update],
  );

  const change = useCallback(
    (values: Partial<AccountPreferenceValues>) => {
      void show(values).then(() => {
        const connected = account.current;
        if (!connected) return;
        const choices: AccountChoices = { ...connected.choices, ...values };
        const language = current.current.language;
        const sequence = ++connected.latest;
        connected.choices = choices;
        connected.queue = connected.queue.then(async () => {
          try {
            await connected.store.save(choices);
            connected.confirmed = { choices, language };
          } catch (error) {
            if (account.current !== connected || sequence !== connected.latest) return;
            const { confirmed } = connected;
            connected.choices = confirmed.choices;
            await show({
              language: confirmed.language,
              digits: confirmed.choices.digits,
              hijri: confirmed.choices.hijri,
            });
            connected.store.onFailure(error);
          }
        });
      });
    },
    [show],
  );

  const setLanguage = useCallback(
    (language: Language) => {
      change({ language });
    },
    [change],
  );
  const setDigits = useCallback(
    (digits: DigitShape | null) => {
      change({ digits });
    },
    [change],
  );
  const setHijri = useCallback(
    (hijri: boolean | null) => {
      change({ hijri });
    },
    [change],
  );

  const setTheme = useCallback(
    (theme: ThemeKey) => {
      void applyTheme(theme).then(() => {
        update({ theme });
      });
    },
    [update],
  );

  const connectAccount = useCallback(
    (choices: AccountChoices, language: Language, store: AccountPreferencesStore) => {
      account.current = { store, choices, confirmed: { choices, language }, queue: Promise.resolve(), latest: 0 };
      void show({ language, digits: choices.digits, hijri: choices.hijri });
    },
    [show],
  );

  const disconnectAccount = useCallback(() => {
    account.current = null;
  }, []);

  const value = useMemo(
    () => ({ ...preferences, setLanguage, setTheme, setDigits, setHijri, connectAccount, disconnectAccount }),
    [preferences, setLanguage, setTheme, setDigits, setHijri, connectAccount, disconnectAccount],
  );
  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- hook belongs with its provider
export function usePreferences(): PreferencesContextValue {
  const value = useContext(PreferencesContext);
  if (!value) {
    throw new Error("usePreferences must be used inside PreferencesProvider");
  }
  return value;
}
